import {observer} from "mobx-react";
import {
    DialogStoreInterface,
    fmsDocumentPrinterStore,
    FmsPrinterItem,
    fmsVlogPrinterStore,
    userPrinterPreferenceStore
} from "../../../store";
import {DialogAction, FieldMultiSelect} from "@kroschke/react-ui";
import {FieldAutoComplete, FieldSelect, ModalDialog} from "../../ui";
import {makeAutoObservable, reaction} from "mobx";
import {ShipmentProcessTypeDto, ShipmentTypeDto} from "../../../api/fms";
import {displayShipmentProcessType, displayShipmentType} from "../../../api";

const processOptions = Object.keys(ShipmentProcessTypeDto).map(key => key as ShipmentProcessTypeDto).map(key => ({
    label: displayShipmentProcessType(key),
    value: key
}))

const shipmentTypeDropDownOptions = Object.keys(ShipmentTypeDto).map(e => ({
    value: e,
    label: displayShipmentType(e as ShipmentTypeDto)
}))

class PrinterMultiSelectDialogStore implements DialogStoreInterface {
    get shipmentTypes(): ShipmentTypeDto[] {
        return this._shipmentTypes;
    }

    set shipmentTypes(value: ShipmentTypeDto[]) {
        this._shipmentTypes = value;
    }
    get process(): ShipmentProcessTypeDto {
        return this._process;
    }

    set process(value: ShipmentProcessTypeDto) {
        this._process = value;
    }

    get documentPrinter(): FmsPrinterItem | undefined {
        return this._documentPrinter;
    }

    set documentPrinter(value: FmsPrinterItem | undefined) {
        this._documentPrinter = value;
    }

    get vlogPrinter(): FmsPrinterItem | undefined {
        return this._vlogPrinter;
    }

    set vlogPrinter(value: FmsPrinterItem | undefined) {
        this._vlogPrinter = value;
    }

    get executing(): boolean {
        return this._executing
    }

    set executing(value: boolean) {
        this._executing = value
    }

    get dialogErrorMessage(): string | undefined {
        return this._dialogErrorMessage
    }

    set dialogErrorMessage(value: string | undefined) {
        this._dialogErrorMessage = value
    }

    get dialogOpen(): boolean {
        return this._dialogOpen
    }

    set dialogOpen(value: boolean) {
        this._dialogOpen = value
    }

    private _dialogOpen: boolean = false
    private _dialogErrorMessage?: string | undefined
    private _executing: boolean = false

    private _vlogPrinter: FmsPrinterItem | undefined
    private _documentPrinter: FmsPrinterItem | undefined
    private _process: ShipmentProcessTypeDto = ShipmentProcessTypeDto.REGULAR
    private _shipmentTypes: ShipmentTypeDto[] = []

    constructor() {
        makeAutoObservable(this)
        reaction(() => [
            userPrinterPreferenceStore.documentPrinterId,
            userPrinterPreferenceStore.vlogPrinterId,
            fmsDocumentPrinterStore.options,
            fmsVlogPrinterStore.options,
        ], () => this.resetDefaultPrinter())
    }

    resetDefaultPrinter() {
        const {documentPrinterId, vlogPrinterId} = userPrinterPreferenceStore
        const documentOptions = fmsDocumentPrinterStore.options
        const vlogOptions = fmsVlogPrinterStore.options

        this.documentPrinter = documentOptions.find(p => p.id === documentPrinterId) || undefined
        this.vlogPrinter = vlogOptions.find(p => p.id === vlogPrinterId) || undefined
    }
}


interface MultiPrinterSelectDialogProps {
    store: PrinterMultiSelectDialogStore
    processSensitive: boolean
    shipmentSensitive: boolean
    labelPrinterSelect: boolean
    title: string
    okLabel: string
    action: () => Promise<any>

}

const MultiPrinterSelectDialog = observer(function MultiPrinterSelectDialog({
                                                                                store,
                                                                                title,
                                                                                okLabel,
                                                                                processSensitive,
                                                                                shipmentSensitive,
                                                                                labelPrinterSelect,
                                                                                action
                                                                            }: MultiPrinterSelectDialogProps) {

    function reset() {
        store.vlogPrinter = undefined
        store.documentPrinter = undefined
        store.shipmentTypes = []
        store.process = ShipmentProcessTypeDto.REGULAR
    }

    const dialogActions: DialogAction[] = [
        {
            label: "Abbrechen",
            execute: async function () {
                reset()
                store.dialogOpen = false
            },
            className: 'is-rounded is-small'
        }
    ]

    let addOk = labelPrinterSelect
        ? Boolean(store.vlogPrinter && store.documentPrinter)
        : Boolean(store.documentPrinter)

    addOk = shipmentSensitive ? (addOk && store.shipmentTypes && store.shipmentTypes.length > 0) : addOk

    if (addOk) {
        dialogActions.push({
            label: okLabel,
            executing: store.executing,
            execute: async function () {
                store.executing = true
                await action()
                reset()
                store.executing = false
                store.dialogOpen = false
            },
            className: 'is-primary is-rounded is-small'
        })
    }

    return <ModalDialog
        titleCloseable={false}
        store={store}
        actions={dialogActions}
        title={title}
    >
        {
            (processSensitive || shipmentSensitive) && <div className={'columns'}>
                {processSensitive && <div className={`column ${shipmentSensitive ? 'is-half' : 'is-full'}`}>
                    <FieldSelect
                        label={'Prozess'}
                        options={processOptions}
                        value={store.process}
                        required={true}
                        onChange={e => {
                            store.process = e as ShipmentProcessTypeDto
                        }}
                    />
                </div>}
                {shipmentSensitive && <div className={`column ${processSensitive ? 'is-half' : 'is-full'}`}>
                    <FieldMultiSelect
                        required={true}
                        label={"Versandart"}
                        options={shipmentTypeDropDownOptions}
                        value={store.shipmentTypes}
                        onChange={value => {
                            store.shipmentTypes = value.map(e => e as ShipmentTypeDto) || []
                        }}
                    />
                </div>}
            </div>}
        <div className={'columns'}>
            <div className={`column is-full`}>
                <FieldAutoComplete
                    label={"Dokumentendrucker"}
                    help={"Bitte wähle den zu verwendenden Drucker"}
                    required={true}
                    itemRenderer={c => c.label}
                    options={fmsDocumentPrinterStore.options}
                    search={async (v) => {
                        await fmsDocumentPrinterStore.find(v)
                        return fmsDocumentPrinterStore.options
                    }}
                    value={store.documentPrinter}
                    onChange={v => {
                        store.documentPrinter = v
                    }}
                />
            </div>
        </div>
        {labelPrinterSelect && <div className={'columns'}>
            <div className={`column is-full`}>
                <FieldAutoComplete
                    label={"Versandlabeldrucker"}
                    help={"Bitte wähle den zu verwendenden Drucker"}
                    required={true}
                    itemRenderer={c => c.label}
                    options={fmsVlogPrinterStore.options}
                    search={async (v) => {
                        await fmsVlogPrinterStore.find(v)
                        return fmsVlogPrinterStore.options
                    }}
                    value={store.vlogPrinter}
                    onChange={v => {
                        store.vlogPrinter = v
                    }}
                />
            </div>
        </div>}
    </ModalDialog>
})

export {
    PrinterMultiSelectDialogStore,
    MultiPrinterSelectDialog
}
